/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import router from '@/router';
import AuthService from '@/services/AuthService.js';
import UserService from '@/services/UserService.js';
import PortalService from '@/services/PortalService.js';
import API from '@/services/API.ts';
import * as Sentry from '@sentry/vue';
import * as z from 'zod';
import { isoDateTimeToDayJs } from '@/types/base-codecs';
export const namespaced = true;
// ============================================================================
// Zod Schemas
// ============================================================================
const domainSchema = z.object({
    id: z.number(),
    name: z.string().catch(''),
    domain: z.string().catch(''),
    baseURL: z.string().catch(''),
    namespace: z.string().catch(''),
    lastUsedAt: isoDateTimeToDayJs.nullish().catch(null),
    lastUsedEmail: z.string().nullish()
}).passthrough();
const userSchema = z.object({
    id: z.number(),
    namespace: z.string().catch(''),
    email: z.string().nullish(),
    username: z.string().nullish(),
    initials: z.string().nullish()
}).passthrough();
const authSessionSchema = z.object({
    id: z.number(),
    domain: domainSchema,
    token: z.string().nullable(),
    user: userSchema,
    sessionCache: z.string().catch('{}')
});
// Ingen .catch([]) här: en enda ogiltig session skulle annars radera alla sparade sessioner. initialise faller tillbaka på rådata.
const authSessionsSchema = z.array(authSessionSchema);
// Delas av samtidiga anrop, t.ex. när flera requests får 401 samtidigt
let logoutPromise = null;
// ============================================================================
// State
// ============================================================================
export const state = {
    users: [],
    isFetchingUsers: false,
    roles: [],
    permissions: [],
    isFetchingRoles: false,
    roleFormDialog: {
        show: false,
        isLoading: false,
        editedRole: null
    },
    userFormDialog: {
        show: false,
        isLoading: false,
        editedUser: null
    },
    userDialog: {
        show: false,
        user: null
    },
    authActiveSessionId: null,
    authSessions: [],
    hasTimeoutError: false,
    hasVersionError: false,
    allDomains: [],
    mainMenuItems: [
        { key: 'dashboard', icon: 'mdi-speedometer', text: 'Dashboard', path: '/' },
        { key: 'checkins-checkouts', icon: 'mdi-format-list-bulleted-square', text: 'In- och utcheckningar', path: '/checkins-checkouts' },
        { key: 'map', icon: 'mdi-map', text: 'Karta', path: '/map' },
        { key: 'calendar', icon: 'coi-bookings-calendar', text: 'Bokningskalender', path: '/calendar' },
        { key: 'bookings', icon: 'mdi-book-marker-outline', text: 'Bokningar', path: '/bookings' },
        { key: 'bookvisit', icon: 'coi-bookvisit', text: 'BookVisit', path: '/bookvisit', badgeContentFunctionName: 'unresolvedBookvisitFailedSyncCount' },
        { key: 'reservations', icon: 'mdi-calendar-text', text: 'Tidsreservationer', path: '/reservations/today' },
        { key: 'cleaning', icon: 'mdi-vacuum', text: 'Städning', path: '/cleaning' },
        { key: 'register', icon: 'mdi-cash-register', text: 'Kassa', path: '/register' },
        { key: 'orders', icon: 'coi-parked-orders', text: 'Parkerade notor', path: '/orders', badgeContentFunctionName: 'orderNotesCount' },
        { key: 'receipts', icon: 'mdi-receipt-text', text: 'Kvitton', path: '/receipts' },
        { key: 'units', icon: 'mdi-desktop-tower-monitor', text: 'Enheter', path: '/units' },
        { key: 'bankgiro', icon: 'coi-bankgirot', text: 'Bankgiro', path: '/bankgiro' },
        { key: 'accounting', icon: 'coi-accounting', text: 'Bokföring', path: '/accounting' },
        { key: 'products', icon: 'mdi-barcode', text: 'Produkter', path: '/products' },
        { key: 'invoices', icon: 'mdi-file-document', text: 'Fakturor', path: '/invoices' },
        { key: 'reports-and-exports', icon: 'mdi-format-list-bulleted', text: 'Listor & Rapporter', path: '/reports-and-exports' },
        { key: 'scr', icon: 'mdi-qrcode-scan', text: 'SCR / CKE', path: '/scr' },
        { key: 'statistics', icon: 'mdi-chart-box-outline', text: 'Nyckeltal & grafer', path: '/statistics' },
        { key: 'sales-reports', icon: 'coi-sales-report', text: 'Försäljningsrapporter', path: '/sales-reports' },
        { key: 'tvv-statistics', icon: 'coi-tillvaxtverket-alt', text: 'Inkvarteringsstatistik', path: '/tvv-statistics' },
        { key: 'settings', icon: 'mdi-cogs', text: 'Inställningar', path: '/settings' }
    ]
};
// ============================================================================
// Mutations (typed with MutationTree<State>)
// ============================================================================
export const mutations = {
    SET_USERS(state, users) {
        state.users = users;
    },
    ADD_OR_REPLACE_USER(state, user) {
        const existingIndex = state.users.findIndex(u => u.id === user.id);
        if (existingIndex !== -1) {
            state.users.splice(existingIndex, 1, user);
        }
        else {
            state.users.push(user);
        }
    },
    REMOVE_USER(state, userId) {
        const index = state.users.findIndex(u => u.id === userId);
        if (index !== -1) {
            state.users.splice(index, 1);
        }
    },
    SET_IS_FETCHING_USERS(state, isFetching) {
        state.isFetchingUsers = isFetching;
    },
    SET_ROLES(state, roles) {
        state.roles = roles;
    },
    ADD_ROLE(state, role) {
        const existingIndex = state.roles.findIndex(r => r.id === role.id);
        if (existingIndex !== -1) {
            state.roles.splice(existingIndex, 1, role);
        }
        else {
            state.roles.push(role);
        }
    },
    REMOVE_ROLE(state, roleId) {
        const index = state.roles.findIndex(r => r.id === roleId);
        if (index !== -1) {
            state.roles.splice(index, 1);
        }
    },
    SET_PERMISSIONS(state, permissions) {
        state.permissions = permissions;
    },
    SET_IS_FETCHING_ROLES(state, isFetching) {
        state.isFetchingRoles = isFetching;
    },
    SET_SHOW_ROLE_FORM_DIALOG(state, show) {
        state.roleFormDialog.show = show;
    },
    SET_ROLE_FORM_DIALOG_LOADING(state, isLoading) {
        state.roleFormDialog.isLoading = isLoading;
    },
    SET_EDITED_ROLE(state, role) {
        state.roleFormDialog.editedRole = role;
    },
    SET_SHOW_USER_FORM_DIALOG(state, show) {
        state.userFormDialog.show = show;
    },
    SET_USER_FORM_DIALOG_LOADING(state, isLoading) {
        state.userFormDialog.isLoading = isLoading;
    },
    SET_EDITED_USER(state, user) {
        state.userFormDialog.editedUser = user;
    },
    SET_SHOW_USER_DIALOG(state, show) {
        state.userDialog.show = show;
    },
    SET_USER_DIALOG_USER(state, user) {
        state.userDialog.user = user;
    },
    SET_USER(state, user) {
        const activeAuthSession = state.authSessions.find(session => session.id === state.authActiveSessionId);
        if (activeAuthSession) {
            activeAuthSession.user = user;
            localStorage.setItem('AUTH_SESSIONS', JSON.stringify(state.authSessions));
        }
    },
    SET_AUTH_SESSION(state, { authSession }) {
        const sessionIndex = state.authSessions.findIndex(session => session.id === authSession.id);
        if (sessionIndex !== -1) {
            state.authSessions.splice(sessionIndex, 1, authSession);
        }
        localStorage.setItem('AUTH_SESSIONS', JSON.stringify(state.authSessions));
    },
    REMOVE_AUTH_SESSION(state, { authSessionId }) {
        const sessionIndex = state.authSessions.findIndex(session => session.id === authSessionId);
        if (sessionIndex !== -1) {
            state.authSessions.splice(sessionIndex, 1);
            localStorage.setItem('AUTH_SESSIONS', JSON.stringify(state.authSessions));
        }
    },
    SET_AUTH_SESSIONS(state, { authSessions }) {
        state.authSessions = authSessions;
        localStorage.setItem('AUTH_SESSIONS', JSON.stringify(authSessions));
    },
    SET_AUTH_ACTIVE_SESSION_ID(state, sessionId) {
        state.authActiveSessionId = sessionId;
        localStorage.setItem('AUTH_ACTIVE_SESSION_ID', sessionId === null ? '' : sessionId.toString());
    },
    SET_HAS_TIMEOUT_ERROR(state, val) {
        state.hasTimeoutError = val;
    },
    SET_HAS_VERSION_ERROR(state, val) {
        state.hasVersionError = val;
    },
    SET_ALL_DOMAINS(state, domains) {
        state.allDomains = domains;
    }
};
// ============================================================================
// Actions (typed with ActionTree<State, RootState>)
// ============================================================================
export const actions = {
    async initialise({ state, commit, dispatch }) {
        let activeAuthSessionId = null;
        const activeAuthSessionIdString = localStorage.getItem('AUTH_ACTIVE_SESSION_ID');
        if (activeAuthSessionIdString) {
            activeAuthSessionId = parseInt(activeAuthSessionIdString, 10);
        }
        if (activeAuthSessionId) {
            // Börjar laddningsanimation tidigt så det inte blir en flash av tom skärm
            commit('SET_IS_FETCHING_DATA', true, { root: true });
        }
        // Always fetch domains to keep them updated (runs async)
        if (!state.allDomains || state.allDomains.length === 0) {
            dispatch('fetchDomains');
        }
        // Load auth sessions from localStorage with Zod validation
        let authSessions = [];
        const authSessionsString = localStorage.getItem('AUTH_SESSIONS');
        if (authSessionsString &&
            authSessionsString !== 'undefined' &&
            authSessionsString !== 'null' &&
            authSessionsString !== '') {
            try {
                const rawParsed = JSON.parse(authSessionsString);
                const parseResult = authSessionsSchema.safeParse(rawParsed);
                if (parseResult.success) {
                    authSessions = parseResult.data;
                }
                else {
                    console.error('Failed to parse authSessions with Zod:', parseResult.error);
                    if (Array.isArray(rawParsed) && rawParsed.length > 0) {
                        console.log('Using raw parsed data as fallback');
                        authSessions = rawParsed;
                    }
                    else {
                        authSessions = [];
                    }
                }
            }
            catch (e) {
                console.error('JSON parse error:', e);
                authSessions = [];
            }
        }
        commit('SET_AUTH_SESSIONS', { authSessions });
        // Validerar att session finns fortfarande
        const activeSession = state.authSessions.find(session => session.id === activeAuthSessionId);
        if (!activeSession && activeAuthSessionId !== 0 && activeAuthSessionId !== null) {
            dispatch('deactivateAuthSession')
                .finally(() => {
                commit('SET_IS_FETCHING_DATA', false, { root: true });
            });
            return false;
        }
        if (!activeSession) {
            commit('SET_IS_FETCHING_DATA', false, { root: true });
            return false;
        }
        dispatch('setActiveSession', { session: activeSession });
        dispatch('fetchData', { quickly: false }, { root: true });
    },
    async fetchDomains({ state, commit, dispatch }) {
        return PortalService.getDomains()
            .then(({ data }) => {
            commit('SET_ALL_DOMAINS', data.data.domains);
            // Skriva över domains i authSessions om de inte är identiska för att få uppdaterad data
            state.authSessions.forEach(session => {
                const updatedDomain = data.data.domains.find((d) => d.id === session.domain.id);
                if (updatedDomain) {
                    if (JSON.stringify(updatedDomain) !== JSON.stringify(session.domain)) {
                        session.domain = updatedDomain;
                        commit('SET_AUTH_SESSION', { authSession: session });
                    }
                }
                else {
                    // domain finns inte kvar längre, logga ut sessionen
                    dispatch('logoutAndDeleteAuthSession', { authSessionId: session.id });
                }
            });
        });
    },
    addNewAuthSession({ state, commit, dispatch }, { user, token, domain }) {
        console.log('addNewAuthSession called with user:', user?.id, 'domain:', domain?.id);
        // Kontrollera om samma user och domain redan finns, i så fall uppdatera existerande istället
        const existingSession = state.authSessions.find(session => session.user.id === user.id && session.domain.id === domain.id);
        if (existingSession) {
            existingSession.token = token;
            existingSession.user = user;
            existingSession.domain = domain;
            commit('SET_AUTH_SESSION', { authSession: existingSession });
            dispatch('setActiveSession', { session: existingSession });
            const sessionCache = existingSession.sessionCache || '{}';
            dispatch('sessionCache/unpackSessionCache', { jsonState: sessionCache }, { root: true });
            return true;
        }
        const newSession = {
            id: Date.now(),
            domain,
            token,
            user,
            sessionCache: '{}'
        };
        const authSessions = state.authSessions.slice();
        authSessions.push(newSession);
        commit('SET_AUTH_SESSIONS', { authSessions });
        dispatch('setActiveSession', { session: newSession });
    },
    setActiveSession({ state, commit, dispatch }, { session }) {
        const activeSession = state.authSessions.find(s => s.id === session?.id);
        if (!session || !activeSession) {
            dispatch('deactivateAuthSession');
            return false;
        }
        const previousSession = state.authSessions.find(s => s.id === state.authActiveSessionId);
        if (previousSession?.id === activeSession.id) {
            return true;
        }
        Sentry.setUser({ username: activeSession.user.username || activeSession.user.email });
        Sentry.setTag('domain', activeSession.domain.baseURL);
        Sentry.setTag('namespace', activeSession.user.namespace);
        API.httpClient.defaults.baseURL = activeSession.domain.baseURL;
        API.httpClient.defaults.headers.common.Authorization = `Bearer ${activeSession.token}`;
        commit('SET_AUTH_ACTIVE_SESSION_ID', session.id);
        if (previousSession && previousSession.id !== activeSession.id) {
            // Har bytt direkt från en session till en annan, ladda om appen för att återställa all state
            window.location.reload(); // Detta är för att återställa vue store, så vi inte sparar state om användaren loggar in på en annan domän
        }
    },
    login({ commit, dispatch }, { username, password, domain }) {
        // Sätter baseURL på axios så inloggning skickas till vald domän
        API.httpClient.defaults.baseURL = domain.baseURL;
        const machineId = window.hardware?.getConfig('AUTH_MACHINE_ID') || localStorage.getItem('AUTH_MACHINE_ID');
        return AuthService.login(username, password, machineId)
            .then(({ data }) => {
            if (data.status === 'success') {
                const user = data.data.user;
                const token = data.data.token;
                dispatch('addNewAuthSession', { user, token, domain });
                dispatch('fetchData', { quickly: false }, { root: true });
                if (user.namespace === 'demo') {
                    commit('SET_OPEN_CONFIRMATION_DIALOG', {
                        header: 'Välkommen till demoversionen',
                        body: 'Tack för att du provar CampingOnline. Vänligen notera att detta är en demoversion, en del funktioner fungerar endast i produktionsmiljön.'
                    }, { root: true });
                }
            }
            return data;
        });
    },
    reconnectSession({ state, commit, dispatch }, { authSessionId }) {
        const existingSession = state.authSessions.find(session => session.id === authSessionId);
        // Påbörja laddningsanimation direkt
        commit('SET_IS_FETCHING_DATA', true, { root: true });
        if (existingSession) {
            const sessionCache = existingSession.sessionCache || '{}';
            dispatch('sessionCache/unpackSessionCache', { jsonState: sessionCache }, { root: true });
        }
        dispatch('setActiveSession', { session: existingSession });
        return AuthService.reconnect()
            .then(({ data }) => {
            if (data.status === 'success') {
                dispatch('fetchData', { quickly: false }, { root: true });
            }
            return data;
        })
            .catch((error) => {
            commit('SET_IS_FETCHING_DATA', false, { root: true });
            console.error('Error reconnecting session', error);
            if (error.isAxiosError && error.response === undefined) {
                // Servern svarade inte. API-interceptorn låser inte längre vid nätverksfel, så gå tillbaka till låsskärmen här
                dispatch('deactivateAuthSession');
            }
            throw error;
        });
    },
    async deactivateAuthSession({ state, commit, dispatch }) {
        const previousSessionId = state.authActiveSessionId;
        // sätter nuvarande aktiva session till inaktiv (men token finns kvar)
        Sentry.setUser({ username: 'logged-out' });
        Sentry.setTag('domain', 'logged-out');
        API.httpClient.defaults.headers.common.Authorization = 'Bearer ';
        // Om activeSessionId redan är null, gör inget
        if (!previousSessionId) {
            return false;
        }
        // Sparar session cache för nuvarande session innan den avaktiveras
        const activeSession = state.authSessions.find(s => s.id === previousSessionId);
        if (activeSession) {
            console.log('Deactivating auth session for user', activeSession.user.username || activeSession.user.email, 'on domain', activeSession.domain.domain);
            const sessionCache = await dispatch('sessionCache/packupSessionCache', undefined, { root: true });
            activeSession.sessionCache = sessionCache.jsonState;
            commit('SET_AUTH_SESSION', { authSession: activeSession });
        }
        commit('SET_AUTH_ACTIVE_SESSION_ID', null);
        // Redirectar till startsidan och laddar om appen
        router.push('/');
        window.location.reload(); // Detta är för att återställa vue store, så vi inte sparar state om användaren loggar in på en annan domän
    },
    logoutAndDeleteAuthSession({ state, commit, dispatch }, { authSessionId }) {
        if (state.authActiveSessionId === authSessionId) {
            // Logga ut nuvarande session
            dispatch('logout')
                .finally(() => {
                commit('REMOVE_AUTH_SESSION', { authSessionId });
            });
            return true;
        }
        const authSession = state.authSessions.find(session => session.id === authSessionId);
        if (!authSession) {
            return false;
        }
        if (authSession.token === null) {
            // Redan utloggad, ta bara bort sessionen
            commit('REMOVE_AUTH_SESSION', { authSessionId });
            return true;
        }
        // Ta bort token från servern innan vi tar bort sessionen
        return AuthService.revokeToken({ token: authSession.token, domain: authSession.domain })
            .then((response) => {
            return response;
        })
            .finally(() => {
            // Kör denna även om revokeToken misslyckas, så token raderas från localStorage oavsett
            commit('REMOVE_AUTH_SESSION', { authSessionId });
        });
    },
    logoutAuthSession({ state, commit, dispatch }, { authSessionId }) {
        if (state.authActiveSessionId === authSessionId) {
            // Logga ut nuvarande session
            dispatch('logout');
            return true;
        }
        // Ta bort annan session
        const authSession = state.authSessions.find(session => session.id === authSessionId);
        if (!authSession) {
            return false;
        }
        return AuthService.revokeToken({ token: authSession.token, domain: authSession.domain })
            .then((response) => {
            return response;
        })
            .finally(() => {
            // Kör denna även om revokeToken misslyckas, så token raderas från localStorage oavsett
            authSession.token = null;
            commit('SET_AUTH_SESSION', { authSession });
        });
    },
    logout({ state, commit, dispatch }) {
        // Logga ut nuvarande session
        const authActiveSessionId = state.authActiveSessionId;
        const authActiveSession = state.authSessions.find(session => session.id === authActiveSessionId);
        if (logoutPromise) {
            return logoutPromise;
        }
        logoutPromise = AuthService.logout()
            .then((response) => {
            return response;
        })
            .finally(() => {
            if (authActiveSession) {
                // Tar bort token från sessionen oavsett om logout lyckades eller inte
                authActiveSession.token = null;
                commit('SET_AUTH_SESSION', { authSession: authActiveSession });
            }
            dispatch('booking/stopActionItemsSyncDiagnostics', undefined, { root: true });
            dispatch('deactivateAuthSession');
            logoutPromise = null;
        });
        return logoutPromise;
    },
    fetchUsers({ commit }) {
        commit('SET_IS_FETCHING_USERS', true);
        UserService.getUsers()
            .then(({ data }) => {
            window.enrich.enrichUser(data.data.users);
            commit('SET_USERS', data.data.users);
        })
            .finally(() => {
            commit('SET_IS_FETCHING_USERS', false);
        });
    },
    deleteUser({ dispatch }, { userId }) {
        return UserService.deleteUser(userId)
            .then((response) => {
            dispatch('fetchUsers');
            return response;
        });
    },
    fetchRoles({ commit }) {
        commit('SET_IS_FETCHING_ROLES', true);
        UserService.getRoles()
            .then(({ data }) => {
            commit('SET_ROLES', data.data.roles);
        })
            .finally(() => {
            commit('SET_IS_FETCHING_ROLES', false);
        });
    },
    fetchPermissions({ commit }) {
        UserService.getPermissions()
            .then(({ data }) => {
            commit('SET_PERMISSIONS', data.data.permissions);
        });
    },
    openNewRoleFormDialog({ commit }) {
        commit('SET_EDITED_ROLE', { id: 0, readable_name: '', permissions: [], hidden_menu_items: [] });
        commit('SET_SHOW_ROLE_FORM_DIALOG', true);
    },
    openEditRoleFormDialog({ commit }, { role }) {
        commit('SET_EDITED_ROLE', {
            id: role.id,
            readable_name: role.readable_name,
            permissions: role.permissions.map(p => p.name),
            hidden_menu_items: role.hidden_menu_items || []
        });
        commit('SET_SHOW_ROLE_FORM_DIALOG', true);
    },
    saveRole({ state, commit }) {
        const editedRole = state.roleFormDialog.editedRole;
        if (!editedRole) {
            return;
        }
        commit('SET_ROLE_FORM_DIALOG_LOADING', true);
        const isNew = editedRole.id === 0;
        const request = isNew
            ? UserService.createRole({ readableName: editedRole.readable_name, permissions: editedRole.permissions, hiddenMenuItems: editedRole.hidden_menu_items })
            : UserService.updateRole({ roleId: editedRole.id, readableName: editedRole.readable_name, permissions: editedRole.permissions, hiddenMenuItems: editedRole.hidden_menu_items });
        return request
            .then(({ data }) => {
            if (data.status === 'success' && data.data) {
                if (!isNew && data.data.role.id !== editedRole.id) {
                    commit('REMOVE_ROLE', editedRole.id); // Om ID har ändrats (vilket är ovanligt men möjligt), ta bort gamla role
                }
                commit('ADD_ROLE', data.data.role);
                commit('SET_SHOW_ROLE_FORM_DIALOG', false);
            }
            return data;
        })
            .finally(() => {
            commit('SET_ROLE_FORM_DIALOG_LOADING', false);
        });
    },
    deleteRole({ commit }, { roleId }) {
        return UserService.deleteRole(roleId)
            .then(({ data }) => {
            if (data.status === 'success') {
                commit('REMOVE_ROLE', roleId);
            }
            return data;
        });
    },
    openUserDialog({ commit }, { user }) {
        commit('SET_USER_DIALOG_USER', user);
        commit('SET_SHOW_USER_DIALOG', true);
    },
    openNewUserFormDialog({ state, commit }) {
        const mostCommonUserColors = state.users.reduce((acc, user) => {
            if (user.color) {
                acc[user.color] = (acc[user.color] || 0) + 1;
            }
            return acc;
        }, {});
        const sortedColors = Object.entries(mostCommonUserColors).sort((a, b) => b[1] - a[1]);
        const defaultColor = sortedColors.length > 0 ? sortedColors[0][0] : 'teal';
        commit('SET_EDITED_USER', {
            id: 0,
            name: '',
            username: '',
            email: '',
            password: '',
            role_id: null,
            color: defaultColor,
            phone_number: '',
            dialling_code_country_id: null
        });
        commit('SET_SHOW_USER_FORM_DIALOG', true);
    },
    openEditUserFormDialog({ commit }, { user }) {
        commit('SET_EDITED_USER', {
            id: user.id,
            name: user.name,
            username: user.username || '',
            email: user.email || '',
            password: '',
            role_id: user.role?.id || null,
            color: user.color || 'teal',
            phone_number: user.phone_number || '',
            dialling_code_country_id: user.dialling_code_country_id || null
        });
        commit('SET_SHOW_USER_FORM_DIALOG', true);
    },
    saveUser({ state, commit, dispatch }) {
        const editedUser = state.userFormDialog.editedUser;
        if (!editedUser) {
            return;
        }
        commit('SET_USER_FORM_DIALOG_LOADING', true);
        const isNew = editedUser.id === 0;
        let request;
        if (isNew) {
            request = UserService.createUser({
                name: editedUser.name,
                username: editedUser.username || undefined,
                email: editedUser.email || undefined,
                password: editedUser.password,
                roleId: editedUser.role_id,
                color: editedUser.color || undefined,
                phoneNumber: editedUser.phone_number || undefined,
                diallingCodeCountryId: editedUser.dialling_code_country_id || undefined
            });
        }
        else {
            request = UserService.updateUser({
                userId: editedUser.id,
                name: editedUser.name,
                username: editedUser.username || undefined,
                email: editedUser.email || undefined,
                password: editedUser.password || undefined,
                roleId: editedUser.role_id,
                color: editedUser.color || undefined,
                phoneNumber: editedUser.phone_number || undefined,
                diallingCodeCountryId: editedUser.dialling_code_country_id || undefined
            });
        }
        return request
            .then(({ data }) => {
            if (data.status === 'success') {
                dispatch('fetchUsers');
                commit('SET_SHOW_USER_FORM_DIALOG', false);
            }
            return data;
        })
            .finally(() => {
            commit('SET_USER_FORM_DIALOG_LOADING', false);
        });
    },
    saveMainMenuConfig({ getters, commit }, { mainMenuConfig }) {
        const user = getters.activeAuthSession?.user;
        if (!user) {
            return Promise.resolve();
        }
        return UserService.updateUserMainMenuConfig({
            userId: user.id,
            mainMenuConfig
        })
            .then(({ data }) => {
            if (data.status === 'success') {
                commit('SET_USER', data.data.user);
            }
            return data;
        });
    }
};
// ============================================================================
// Getters
// ============================================================================
export const getters = {
    isAuthenticated(state, getters) {
        return !!getters.activeAuthSession?.token;
    },
    activeAuthSession(state) {
        return state.authSessions.find(session => session.id === state.authActiveSessionId);
    },
    isSupport(state, getters) {
        return getters.activeAuthSession?.user.is_support === true;
    },
    isAdmin(state, getters) {
        return getters.activeAuthSession?.user.role?.name === 'admin';
    },
    hasPermission: (state, getters) => (permissionName) => {
        const role = getters.activeAuthSession?.user.role;
        return getters.isAdmin || (role?.permissions?.some((permission) => permission.name === permissionName) ?? false);
    },
    isMenuItemRestricted: (state, getters) => (menuItemKey) => {
        const restrictedMenuItemKeys = getters.activeAuthSession?.user.role?.hidden_menu_items || [];
        return restrictedMenuItemKeys.includes(menuItemKey);
    },
    activeMainMenuItems: (state, getters) => {
        if (!getters.activeAuthSession) {
            return [];
        }
        if (!getters.activeAuthSession.user.main_menu_config) {
            return [];
        }
        const mainMenuConfig = getters.activeAuthSession.user.main_menu_config;
        const activeMenuItemKeys = mainMenuConfig.active_main_menu_items || []; // Ovanför "Mer"
        const menuItemOrder = mainMenuConfig.menu_item_order || [];
        const hiddenMenuItemKeys = getters.activeAuthSession.user.hidden_main_menu_items || []; // Dolda av användaren
        const unauthorizedMenuItemKeys = getters.activeAuthSession.user.role?.hidden_menu_items || []; // Dolda av rollen
        const orderedMenuItems = ([
            ...menuItemOrder.map((key) => state.mainMenuItems.find(item => item.key === key)).filter(Boolean),
            ...state.mainMenuItems.filter(item => !menuItemOrder.includes(item.key))
        ]);
        return orderedMenuItems.filter(item => activeMenuItemKeys.includes(item.key) && !unauthorizedMenuItemKeys.includes(item.key) && !hiddenMenuItemKeys.includes(item.key));
    },
    inactiveMainMenuItems: (state, getters) => {
        if (!getters.activeAuthSession) {
            return [];
        }
        if (!getters.activeAuthSession.user.main_menu_config) {
            return [];
        }
        const mainMenuConfig = getters.activeAuthSession.user.main_menu_config;
        const activeMenuItemKeys = mainMenuConfig.active_main_menu_items || []; // Ovanför "Mer"
        const menuItemOrder = mainMenuConfig.menu_item_order || [];
        const hiddenMenuItemKeys = mainMenuConfig.hidden_main_menu_items || []; // Dolda av användaren
        const unauthorizedMenuItemKeys = getters.activeAuthSession.user.role?.hidden_menu_items || []; // Dolda av rollen
        const orderedMenuItems = ([
            ...menuItemOrder.map((key) => state.mainMenuItems.find(item => item.key === key)).filter(Boolean),
            ...state.mainMenuItems.filter(item => !menuItemOrder.includes(item.key))
        ]);
        return orderedMenuItems.filter(item => !activeMenuItemKeys.includes(item.key) && !unauthorizedMenuItemKeys.includes(item.key) && !hiddenMenuItemKeys.includes(item.key));
    },
    hiddenMainMenuItems: (state, getters) => {
        if (!getters.activeAuthSession) {
            return [];
        }
        if (!getters.activeAuthSession.user.main_menu_config) {
            return [];
        }
        const mainMenuConfig = getters.activeAuthSession.user.main_menu_config;
        const hiddenMenuItemKeys = mainMenuConfig.hidden_main_menu_items || []; // Dolda av användaren
        const unauthorizedMenuItemKeys = getters.activeAuthSession.user.role?.hidden_menu_items || []; // Dolda av rollen
        const menuItemOrder = mainMenuConfig.menu_item_order || [];
        const orderedMenuItems = ([
            ...menuItemOrder.map((key) => state.mainMenuItems.find(item => item.key === key)).filter(Boolean),
            ...state.mainMenuItems.filter(item => !menuItemOrder.includes(item.key))
        ]);
        return orderedMenuItems.filter(item => hiddenMenuItemKeys.includes(item.key) || unauthorizedMenuItemKeys.includes(item.key));
    }
};
